import { Fab, Webchat } from '@botpress/webchat'
import { useMemo } from 'react'
import { EmptyState, Spinner } from '~/elements'
import { getIntegrationsFromBot } from '~/features/bots/services'
import { useCurrentUser, useFeature, useIsAuthorized, useUpdateBot } from '~/hooks'
import { useQuery, useSuspenseQuery } from '~/services'
import { config, getCdmStudioUrl } from '~/shared/configurations'
import semver from 'semver'
import { getUseConfigStore } from '~/features/webchat/stores/useConfigStore'
import { MessageSquareOff } from 'lucide-react'
import { Button, Card } from '@bpinternal/ui-kit-next'
import WebchatEmptyStateWrapper from '~/elements/WebchatEmptyStateWrapper'

const LATEST_WEBCHAT_VERSION = '0.3.0'

export const WebchatContent = ({ botId, workspaceId }: { botId: string; workspaceId: string }) => {
  const bot = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_', { botId, workspaceId }).data
  const user = useCurrentUser()

  const { data: script, isError } = useQuery('workspaces_/$workspaceId_/bots_/$botId_/webchat', {
    botId,
    workspaceId,
    type: 'fullscreen',
  })

  const { mutate: updateBot } = useUpdateBot()

  const webchatInstalledIntegration = Object.values(bot.integrations).find(
    (integration) => integration.name === 'webchat'
  )

  const isAuthorized = useIsAuthorized({ workspaceId, userId: user.id })
  const isAuthorizedToOpenStudio = isAuthorized('studio.view') || isAuthorized('studio.edit')

  const webchatIntegration = getIntegrationsFromBot(bot).find((integration) => integration.name === 'webchat')
  const isLatestWebchatVersion = semver.gte(webchatIntegration?.version ?? '0.0.0', LATEST_WEBCHAT_VERSION)

  const useConfigStore = useMemo(() => getUseConfigStore({ botId, workspaceId }), [botId, workspaceId])

  const botName = useConfigStore((state) => state.botName)
  const botAvatar = useConfigStore((state) => state.botAvatar)
  const botDescription = useConfigStore((state) => state.botDescription)
  const feedbackEnabled = useConfigStore((state) => state.feedbackEnabled)
  const soundEnabledInConfig = useConfigStore((state) => state.soundEnabled)

  const composerPlaceholder = useConfigStore((state) => state.composerPlaceholder)
  const allowFileUpload = useConfigStore((state) => state.allowFileUpload)
  const fabImage = useConfigStore((state) => state.fabImage)
  const footer = useConfigStore((state) => state.footer)
  const storageLocation = useConfigStore((state) => state.storageLocation)
  const email = useConfigStore((state) => state.email)
  const phone = useConfigStore((state) => state.phone)
  const termsOfService = useConfigStore((state) => state.termsOfService)
  const privacyPolicy = useConfigStore((state) => state.privacyPolicy)
  const website = useConfigStore((state) => state.website)
  const themeMode = useConfigStore((state) => state.themeMode)
  const radius = useConfigStore((state) => state.radius)
  const color = useConfigStore((state) => state.color)
  const fontFamily = useConfigStore((state) => state.fontFamily)
  const headerVariant = useConfigStore((state) => state.headerVariant)
  const variant = useConfigStore((state) => state.variant)
  const customStyleSheet = useConfigStore((state) => state.customStyleSheet)

  const pushpinUpgrade = useFeature('pushpin-upgrade')

  if (!(bot.deployedAt || bot.dev) || !webchatIntegration) {
    return (
      <WebchatEmptyStateWrapper>
        <EmptyState
          iconSize={2}
          title={'Chatbot not published'}
          description={'You must publish the chatbot from Botpress Studio to be able to interact with it.'}
          primaryAction={
            isAuthorizedToOpenStudio ? (
              <a href={getCdmStudioUrl(bot.id)} target="_blank" rel="noopener noreferrer">
                <Button disabled={!isAuthorizedToOpenStudio} size="sm" className="sm:size-default">
                  Edit in studio
                </Button>
              </a>
            ) : undefined
          }
        />
      </WebchatEmptyStateWrapper>
    )
  }

  if (!isLatestWebchatVersion) {
    return (
      <WebchatEmptyStateWrapper>
        <Card className="flex h-full w-full items-center justify-center">
          <EmptyState
            iconSize={2}
            title={'Webchat integration outdated'}
            description={'You must upgrade the webchat integration to interact with it in the Dashboard.'}
          />
        </Card>
      </WebchatEmptyStateWrapper>
    )
  }

  function enableWebchat(webchatIntegrationId: string) {
    updateBot({
      id: bot.id,
      workspaceId,
      integrations: { [webchatIntegrationId]: { enabled: true, configuration: {} } },
    })
  }

  if (!(script && webchatIntegration) && !isError) {
    return (
      <div className="flex h-[400px] w-full max-w-sm items-center justify-center sm:h-full">
        <Spinner />
      </div>
    )
  }

  if (!webchatIntegration?.enabled) {
    return (
      <div className="flex h-[400px] w-full max-w-sm flex-col items-center justify-center sm:flex-1">
        <EmptyState
          icon={MessageSquareOff}
          title="Webchat disabled"
          description="You must enable the Webchat integration to interact with it."
        />
        <div className="flex flex-1">
          <div>
            <Button onClick={() => enableWebchat(webchatIntegration.id)} size="sm" className="sm:size-default">
              Enable Webchat
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <WebchatEmptyStateWrapper>
      <Webchat
        configuration={{
          botName,
          botAvatar,
          botDescription,
          feedbackEnabled,
          soundEnabled: soundEnabledInConfig,
          composerPlaceholder,
          footer,
          storageLocation,
          email,
          phone,
          website,
          privacyPolicy,
          termsOfService,
          radius,
          color,
          fontFamily,
          variant,
          headerVariant,
          themeMode,
        }}
        allowFileUpload={allowFileUpload}
        apiUrl={pushpinUpgrade ? 'https://pushpin.botpress.cloud' : config.webchatUrl}
        clientId={webchatInstalledIntegration?.webhookId ?? ''}
        storageKey={`bp-webchat-${botId}`}
      />
      <div className="mb-5 ml-auto h-16 w-16 flex-none">
        <Fab imgUrl={fabImage} />
      </div>
      <style>{customStyleSheet}</style>
    </WebchatEmptyStateWrapper>
  )
}
